import {
  workspaceInformationPlacement,
  type WorkspaceInformationPlacement,
} from '@kop/navigation-policy/workspace-projection';
import type { InformationDomainId, Persona } from '@kop/navigation-policy';
import type { DutyCapability, Persona as AccountPersona, ResourceScope } from '@kop/contracts';
import type { WorkspaceRole } from './auth';
import {
  featureAccessProjectionStatus,
  isIndependentSelfServiceRoute,
  managedFeatureForRoute,
} from './feature-access-projection';
import { workspaceFeatureMounted } from './workspace-route-policy';
import { canReadHeadquartersFactoryFeature } from './headquarters-factory-read';

type NavigableFeature = Readonly<{ id: string; label: string }>;

const personaByAccount: Readonly<Partial<Record<AccountPersona, Persona>>> = {
  'site-manager': 'site_principal',
  'safety-manager': 'site_safety',
  'site-staff': 'site_principal',
  'safety-staff': 'site_safety',
  'partner-admin': 'contractor_manager',
  'hq-safety-admin': 'hq_safety',
  'hq-staff': 'hq_audit',
  'hq-auditor': 'hq_audit',
  'system-admin': 'system_administrator',
};

const domainByFeature = {
  'electronic-approval': 'central-approval',

  'shared-drive': 'shared-resources',

} as const;

const headquartersGeneralFeatures = new Set([
  'electronic-approval',
  'shared-drive',
]);

const headquartersFactoryFeatures: Readonly<Record<string, DutyCapability>> = {
  'inspection-reporting': 'document:read',
  'inspection-log': 'document:read',
  'inspection-unresolved': 'document:read',

  'finding-action': 'document:read',
  tbm: 'document:read',
  incident: 'document:read',
  'regular-risk': 'document:read',

  'work-permit': 'document:read',
  'safety-training': 'education:read',
  'evacuation-drill': 'document:read',
  'chemical-msds-management': 'msds:read',
  'safety-cost': 'document:read',
  'meeting-minutes': 'document:read',

  'safety-board': 'document:read',
  'shared-drive': 'document:read',

  'electronic-approval': 'approval:read',
};

const siteGeneralFeatures = new Set([
  'tbm',
  'equipment-dispatch',
  'electronic-approval',
  'shared-drive',
]);

export function projectWorkspaceNavigation<T extends NavigableFeature>(
  candidates: readonly T[],
  context: {
    role: WorkspaceRole;
    persona: AccountPersona;
    capabilities: readonly DutyCapability[];
    resource?: ResourceScope;
    featureAccess?: unknown;
  },
): readonly T[] {
  const featureAccess = featureAccessProjectionStatus(context.featureAccess);
  if (featureAccess.kind === 'enforced')
    return candidates.filter((candidate) => {
      if (!workspaceFeatureMounted(candidate.id)) return false;
      if (isIndependentSelfServiceRoute(candidate.id)) return true;
      const managedFeature = managedFeatureForRoute(candidate.id);
      return managedFeature !== null && (featureAccess.features.has(managedFeature) ||
        (context.resource !== undefined && canReadHeadquartersFactoryFeature({
          id: 'navigation', label: '', detail: '', role: context.role, persona: context.persona,
          capabilities: context.capabilities, resource: context.resource, featureAccess: context.featureAccess,
        }, candidate.id)));
    });
  if (featureAccess.kind === 'malformed')
    return candidates.filter((candidate) => isIndependentSelfServiceRoute(candidate.id));
  const persona = personaByAccount[context.persona];
  if (!persona) return [];
  return candidates.flatMap((feature) => {
    if (!workspaceFeatureMounted(feature.id)) return [];
    if (
      context.resource?.kind === 'site' &&
      ['hqSafety', 'hqAudit'].includes(context.role) &&
      ['hq-safety-admin', 'hq-staff', 'hq-auditor'].includes(context.persona)
    ) {
      const capability = headquartersFactoryFeatures[feature.id];
      return capability && context.capabilities.includes(capability) ? [feature] : [];
    }
    if (
      (context.persona === 'hq-staff' || context.persona === 'hq-auditor') &&
      !headquartersGeneralFeatures.has(feature.id)
    )
      return [];
    if (context.persona === 'site-staff' && !siteGeneralFeatures.has(feature.id)) return [];
    if (['organisation-contacts', 'site-map', 'safety-design'].includes(feature.id)) return [];
    if (feature.id === 'safety-content' && context.persona !== 'safety-manager') return [];

    const domain = domainByFeature[feature.id as keyof typeof domainByFeature];
    if (!domain) return [feature];
    const placement: WorkspaceInformationPlacement | null = workspaceInformationPlacement(
      persona,
      domain as InformationDomainId,
    );
    if (!placement || placement.placement === 'contextual-only') return [];
    if (feature.id === 'electronic-approval' && !context.capabilities.includes('approval:read'))
      return [];

    return [{ ...feature, label: placement.label ?? feature.label }];
  });
}
